import { RenderFunction } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import { SEED_TO_SALE_VENDORS } from '@/constants/seedToSale';
import { Address } from '@/models/address';
import { Money } from '@/models/money';
import { ExtractObjectValues } from '@/utilityTypes/extractObjectValues';

import { ShippingChargeOrOrderMinimum, TaxInfo } from './distributor';

/**
 * The parent entity in the LeafLink ecosystem
 */
export interface Company extends Address {
  activeCampaign?: unknown;
  apiEnabled: boolean;
  applyArmsLengthTax: boolean;
  autoIncrementOrderIds: boolean;
  availableLanguages?: string[][];
  availableLanguagesTranslated?: string[][];
  defaultLanguage: string[];
  defaultLanguageTranslated: string[];
  taxInfo: TaxInfo;
  billing: Billing;
  isMetrcCtaAvailable: boolean;
  isLogisticsVisible: boolean;
  enableOrderLocking: boolean;
  orderLockState: string;
  isPre2018Ca: boolean;
  defaultTimezone: string;
  defaultPaymentOnSellThroughPaymentTerm?: Partial<PaymentTerm>;
  addressesConfirmedBy?: number | null;
  addressesConfirmedOn?: string | null;
  billingStatus: CompanyBillingStatus | null;
  orderMinimum: ShippingChargeOrOrderMinimum;
  hardOrderMinimum: boolean;
  accountingSystem?: string | null;
  accountingSystemOther?: string | null;
  crmSystem?: string | null;
  crmSystemOther?: string | null;
  salesforceId: string;
  externalSystemConsent: boolean;
  brandsForPreview: {
    id: number;
    name: string;
    friendlySlug: string;
  }[];
  businessIdentificationNumber: string;
  businessLicenseName: string | null;
  integrateSystem?: string | null;
  inventorySystem?: string | null;
  inventorySystemOther?: string | null;
  buyer?: boolean;
  canUsePurchaseList?: boolean;
  canViewProductActivities: boolean;
  corporateAddress?: Address;
  s2sTrackingSystem?: null;
  s2sTrackingSystemOther?: null;
  dba: string;
  azComplianceFeatures: boolean;
  deliveryPreferences?: string;
  enableAdvancedReportingSigmaCatman?: boolean;
  enableRetailerCategoryManagement?: boolean;
  enableBrandFulfillment: boolean;
  enableBrandFulfillmentMultiFacilityInventory: boolean;
  enableBrandFulfillmentLotInventoryVisibility?: boolean;
  enableChiliPiperForState?: boolean;
  enableDeliveries: boolean;
  enableFacilitySelectionOrderCreation: boolean;
  enablePackingApp: boolean;
  enablePaymentsDirect: boolean;
  enablePaymentsFlexible: boolean;
  enablePost?: boolean;
  isPaymentOnSellThroughDefault?: boolean;
  enableTraceabilityService: boolean;
  deliveryAddress?: Address;
  description: string;
  enableCertifiedSeller: boolean;
  freeShippingMinimum?: Money;
  fullDisplayName: string;
  group: CompanyGroup | null;
  hasArchivedLicenses: boolean;
  hasBrandOwnerOrdersEnabled?: boolean;
  hasWarnings: boolean;
  id: number;
  image?: string | null;
  isAccountOwner?: boolean;
  isBrandOwner?: boolean;
  isDeliveryProvider: boolean;
  isLive: boolean;
  liveDate: string | null;
  liveDateIso: string | null;
  isLogisticsEnabledForState: boolean;
  isLogisticsOnly: boolean;
  isLogisticsSplashVisible: boolean;
  isShippingMetrcKeyValid?: boolean;
  licenses?: License[];
  archivedLicenses?: License[];
  manageInventoryInCases?: boolean;
  name: string;
  retailerInventory: boolean;
  seller?: boolean;
  shippingCharge?: Money;
  shippingMetrcKey?: string;
  siteBanner: SiteBanner;
  slug: string;
  status: CompanyStatus;
  statusReason: CompanyStatusReasons;
  stsEnabled: boolean;
  submittedOrdersCount: number;
  unreadMessageCount: number;
  webhooksEnabled: boolean;
  website?: string | null;
  staffIsAdmin?: boolean;
  seedToSaleVendor?: ExtractObjectValues<typeof SEED_TO_SALE_VENDORS> | null;
  erpSystem?: string | null;
  erpSystemOther?: string | null;
  reportingSystem?: string | null;
  reportingSystemOther?: string | null;
  posSystem?: string | null;
  posSystemOther?: string | null;
  igHandle?: string | null;
  fbHandle?: string | null;
  twitterHandle?: string | null;
  ytHandle?: string | null;
  phone?: string;
  phoneExtension?: string;
  itSystem?: string | null;
  itSystemOther?: string | null;
  s2STrackingSystem?: string | null;
  s2STrackingSystemOther?: string | null;
  logisticsSystem?: string | null;
  logisticsSystemOther?: string | null;
  announcement: string | null;
  applyAncillaryTax?: boolean;
  batchEnabled: boolean;
  businessIdentifier: null | string;
  hasAccting: boolean;
  hideAttachedPackages: boolean;
  hasEnabledSampleRequestsByDefault?: boolean;
}

export interface BadgeCount {
  acceptedOrders: number;
  activeOrders: number;
  backorders: number;
  collaborativeOrders: number;
  combinedOrders: number;
  completeOrders: number;
  draftOrders: number;
  fulfilledOrders: number;
  pendingRequests: number;
  shippedOrders: number;
  submittedOrders: number;
  submittedReturns: number;
  totalFromLast7Days: TotalFromLastSevenDays;
}

export interface Billing {
  daysOutstanding: number;
  oldestDueDate: string;
  pausingDaysOutstanding: number;
  unpaidOverdueCount: number;
}

interface TotalFromLastSevenDays {
  currency: string;
  total: number;
}

export interface BrandForm {
  id?: number;
  name?: string;
  website?: string;
  tagline?: string;
  phone?: string;
  orderMinimum?: number;
  orderMinimum0?: number;
  orderMinimum1?: string;
  description?: string;
  announcement?: string;
  fbHandle?: string;
  igHandle?: string;
  twitterHandle?: string;
  ytHandle?: string;
  image?: string;
  banner?: string;
  address?: string;
  suite?: string;
  city?: string;
  zipcode?: string;
  state?: number;
}

export interface Brand {
  id: number;
  name: string;
  company?: number;
  description?: string;
  image?: string;
  banner?: string;
}

export interface PromoCode {
  id?: number;
  code: string | null;
  isAutomatic: boolean;
  isPublic: boolean;
  brand: number;
  displayName?: string;
  discountType: '$' | '%';
  discountAmount: Money | string;
  reusable: boolean;
  startDate: string;
  endDate?: string | null;
  orderMinimumType: number;
  orderMinimum?: string | null;
  isBuyersClub?: boolean;
}

export interface License {
  expirationDate?: string; // // should not be optional
  id: number;
  issueDate?: string; // should not be optional
  number: string;
  type: LicenseType | string;
  warning?: string | null;
  classification?: string; // Not part of company.licenses, leaving for pre-existing references
  displayType?: string; // Not part of company.licenses, leaving for pre-existing references
}

export type BuyerLicense = Pick<License, 'number' | 'type'>;
export type SellerLicense = Omit<License, 'displayType' | 'warning'>;

export interface LicenseType {
  id: number;
  activeForRegistration?: boolean;
  classification: string;
  displayType: string;
  hasMedicalLineItems: boolean;
  state: string;
  type: string;
  typeCode: string;
}

export interface CompanyLicense {
  id: number;
  number: string;
  company?: number;
  archived: boolean;
  companyLicenseId: number;
  expirationDate: string;
  issueDate: string;
  public: boolean;
  document?: string | null;
  type: LicenseType;
}

export interface CompanyLicenseWithStatus extends CompanyLicense {
  status?: string;
}

export interface Application {
  id: number;
  name: string;
  description: string;
  developerEmail: string;
  groups: number[];
}

export interface Contacts {
  users: Array<{
    firstName: string;
    lastName: string;
    email: string;
    name: string;
    isNotificationRecipient: boolean;
  }>;
  notificationType: string;
}

export interface ShippingPreferences {
  serviceHours: ServiceHours[];
  instruction: string;
  contacts: Contacts;
}

export interface ServiceHours {
  dayOfTheWeek: number;
  openTime?: string;
  closeTime?: string;
  isClosed: boolean;
}

export interface GlobalBanner {
  /**
   * A full or relative href string. Results in a full/hard page load.
   */
  link?: string;
  to?: RouteLocationRaw;
  callToAction: string;
  canClose: boolean;
  priority: 'low' | 'medium' | 'high';
  name: string;
  color: 'blue' | 'teal' | 'royal' | 'orange' | 'green' | 'red';
  message: string | RenderFunction;
  // number of days until this banner will reappear once dismissed.
  reshow: number;
  openInNewTab?: boolean;
}

export interface SiteBanner {
  callToAction: string;
  content: string;
  dates: {
    startDate: string;
    endDate: string;
  };
  link: string;
  openInNewTab: number;
}

export interface CompanyGroup {
  enableReplenishment: boolean;
  enableAdvancedReportingTableauReplenishment: boolean;
  id: number;
  name: string;
}

export interface PaymentTerm {
  code?: string;
  createdOn?: string;
  days?: number | null;
  description?: string;
  id: number;
  modified?: string;
  term: string;
}

export interface PaymentMethod {
  code?: string;
  createdOn?: string;
  description?: string;
  id: number;
  method: string;
  modified?: string;
}

export type PaymentProgram = 'flexible' | 'direct' | null;

export type PaymentStrategy = 'direct' | 'factored' | 'reverse_factored' | null;

export interface PaymentOption {
  id: number;
  isDefault: boolean;
  isOptIn?: boolean;
  isPaymentOnSellThrough?: boolean;
  paymentMethod: PaymentMethod;
  paymentProgram: PaymentProgram;
  paymentStrategy: PaymentStrategy;
  paymentTerm: PaymentTerm;
}

export interface PaymentOptionError {
  id: number;
}

export type PaymentOptionPayload = Omit<PaymentOption, 'id' | 'paymentMethod' | 'paymentTerm'>;

export interface CompanyPaymentOptionsFetchParams {
  id?: number;
  active?: boolean;
}

export interface UpdateMetrcKeyPayload {
  companyId: number;
  metrcKeyData: boolean;
}

export enum CompanyStatuses {
  Active = 'active',
  Available = 'available',
  Inactive = 'inactive',
  Unavailable = 'unavailable',
}

export type CompanyStatus = `${CompanyStatuses}`;

export enum CompanyBillingStatuses {
  Goodstanding = 'goodstanding',
  Outstanding = 'outstanding',
  Delinquent = 'delinquent',
  Churned = 'churned',
  NoRelationship = 'norelationship',
}

export type CompanyBillingStatus = `${CompanyBillingStatuses}`;

//this api is mocked so whenever it's updated we will have to change a few things here
export interface PendingStoreRequest {
  companyInformation?: string;
  id: number;
  licenseInfo: string;
  notes?: string;
  phone: string;
  posSystem: string;
  status: string;
}

export enum CompanyStatusReason {
  Default = 'default',
  LicenseInactive = 'license_inactive',
  Reactivation = 'reactivation',
}

export type CompanyStatusReasons = `${CompanyStatusReason}`;

export const SUPPORTED_INVENTORY_INSIGHTS_POS_SYSTEMS = ['Dutchie'];

export const POS_OPTIONS = ['Dutchie', 'Greenbits', 'Blaze', 'Cova', 'Meadow', 'GrowFlow', 'Flowhub', 'Other'] as const;
export type PosOption = (typeof POS_OPTIONS)[number];

export interface CustomerCompany {
  id: number;
  displayName: string;
  delinquent: boolean;
  extAcctId: number;
}

export interface SellerForBuyer {
  id: number;
  name?: string;
  sellThroughDataShared?: boolean;
  excludedFromPost?: boolean;
}

export interface EligibleCompany {
  address: string;
  city: string;
  id: string | number;
  name: string;
}

export interface CompanyExcludedFromPost {
  id: number;
  dba: string;
  name: string;
  excludedFromPost: boolean;
}
